const ACCESS_TOKEN_KEY = 'access_token';
const REFRESH_TOKEN_KEY = 'refresh_token';
const USER_KEY = 'user';
const PENDING_OTP_EMAIL_KEY = 'pending_otp_email';
const PENDING_OTP_MESSAGE_KEY = 'pending_otp_message';

const isBrowser = () => typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';

const safeGetItem = (key) => {
  if (!isBrowser()) return null;

  try {
    return window.localStorage.getItem(key);
  } catch (error) {
    console.warn('Unable to access localStorage:', error);
    return null;
  }
};

const safeGetSessionItem = (key) => {
  if (!isBrowser() || !window.sessionStorage) return null;
  try {
    return window.sessionStorage.getItem(key);
  } catch (error) {
    console.warn('Unable to access sessionStorage:', error);
    return null;
  }
};

const safeSetSessionItem = (key, value) => {
  if (!isBrowser() || !window.sessionStorage) return;
  try {
    if (value == null) window.sessionStorage.removeItem(key);
    else window.sessionStorage.setItem(key, value);
  } catch (error) {
    console.warn('Unable to write to sessionStorage:', error);
  }
};

const safeSetItem = (key, value) => {
  if (!isBrowser()) return;

  try {
    if (value === undefined || value === null) {
      window.localStorage.removeItem(key);
    } else {
      window.localStorage.setItem(key, value);
    }
  } catch (error) {
    console.warn('Unable to write to localStorage:', error);
  }
};

export const getAccessToken = () => safeGetItem(ACCESS_TOKEN_KEY);

export const getRefreshToken = () => safeGetItem(REFRESH_TOKEN_KEY);

/** Pending OTP email (sessionStorage) – set after login when requireOtp, cleared after verify-otp or logout */
export const getPendingOtpEmail = () => safeGetSessionItem(PENDING_OTP_EMAIL_KEY);
export const setPendingOtpEmail = (email) => safeSetSessionItem(PENDING_OTP_EMAIL_KEY, email || null);

/** Message from login response when OTP is required (e.g. "Check your email for the code") */
export const getPendingOtpMessage = () => safeGetSessionItem(PENDING_OTP_MESSAGE_KEY);
export const setPendingOtpMessage = (message) => safeSetSessionItem(PENDING_OTP_MESSAGE_KEY, message || null);
export const clearPendingOtpMessage = () => safeSetSessionItem(PENDING_OTP_MESSAGE_KEY, null);

export const clearPendingOtpEmail = () => {
  safeSetSessionItem(PENDING_OTP_EMAIL_KEY, null);
  clearPendingOtpMessage();
};

export const getStoredUser = () => {
  const raw = safeGetSessionItem(USER_KEY) || safeGetItem(USER_KEY);

  if (!raw) return null;

  try {
    return JSON.parse(raw);
  } catch (error) {
    console.warn('Unable to parse stored user:', error);
    return null;
  }
};

export const setStoredUser = (user) => {
  if (!user) {
    safeSetItem(USER_KEY, null);
    return;
  }

  try {
    safeSetItem(USER_KEY, JSON.stringify(user));
  } catch (error) {
    console.warn('Unable to store user:', error);
  }
};

export const setAuthSession = ({ accessToken, refreshToken, user }) => {
  safeSetItem(ACCESS_TOKEN_KEY, accessToken || null);
  safeSetItem(REFRESH_TOKEN_KEY, refreshToken || null);

  if (user) {
    safeSetItem(USER_KEY, JSON.stringify(user));
  }
};

/**
 * Clear all authentication-related data from localStorage
 * This includes tokens, user data, and any other M&E system related data
 */
export const clearAuthSession = () => {
  // Clear main auth keys
  safeSetItem(ACCESS_TOKEN_KEY, null);
  safeSetItem(REFRESH_TOKEN_KEY, null);
  safeSetItem(USER_KEY, null);
  clearPendingOtpEmail();

  // Clear any other M&E related localStorage items
  if (isBrowser()) {
    try {
      // Get all keys that start with 'mel_'
      const keysToRemove = [];
      for (let i = 0; i < window.localStorage.length; i++) {
        const key = window.localStorage.key(i);
        if (key && key.startsWith('mel_')) {
          keysToRemove.push(key);
        }
      }
      
      // Remove all M&E related keys
      keysToRemove.forEach(key => {
        window.localStorage.removeItem(key);
      });
    } catch (error) {
      console.warn('Error clearing additional localStorage items:', error);
    }
  }
};


